import { persistentAtom } from '@nanostores/persistent';
import { atom, computed, onSet, type ReadableAtom } from 'nanostores';

import { clamp } from '@iconik-io/core/src/clamp';
import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

import {
  AUDIO_MIXER_MAX_CHANNELS,
  LIVE_EDGE_THRESHOLD_SECONDS,
} from '../../constants';
import type {
  PlayerEvents,
  PlayerMediaPasswordEvent,
} from '../../player_events';
import type { SyncClient } from '../../sync/client/sync_client';
import { syncAtom } from '../../sync/sync_atom';
import type {
  AudioChannel,
  PlaybackTrajectory,
  Subtitle,
  TimeFormat,
  VariantType,
} from '../../types';
import { getFrameAlignedTime as getAlignedTime } from '../../utils/get_frame_aligned_time';
import { getPreferredPlaylistItemVariant } from '../../utils/get_preferred_playlist_item_variant';
import { isMultiChannelAudioSupported } from '../../utils/is_multi_channel_audio_supported';
import {
  createPersistentAudioChannelsEncoder,
  genericEncoder,
  numberEncoder,
} from './persistent_encoding';
import type { createPlaylistStore } from './playlist';

export type UnlockMediaPasswordStatus = 'need_password' | 'incorrect_password';

const playbackRates: Record<PlaybackTrajectory, number[]> = {
  forward: [1, 1.25, 1.5, 1.75, 2, 4, 8, 16],
  forward_slow: [0.75, 0.5, 0.25],
  backward: [-1, -1.25, -1.5, -1.75, -2, -4, -8],
  backward_slow: [-0.75, -0.5, -0.25],
};

const logger = DebugLogger.getInstance('unified_player_playable_media');

const availableRates = Object.values(playbackRates)
  .flat()
  .filter((value, index, array) => array.indexOf(value) === index)
  .sort((a, b) => a - b);

// Default to channel 1 left, channel 2 right, others center
const initialAudioChannels: AudioChannel[] = Array.from(
  { length: AUDIO_MIXER_MAX_CHANNELS },
  (_, index) => ({
    volume: 1,
    pan: index === 0 ? -1 : index === 1 ? 1 : 0,
    isMuted: false,
    isSolo: false,
  }),
);

const initialPreferredVariants: VariantType[] = isMultiChannelAudioSupported()
  ? ['multi_channel_audio_proxy', 'proxy', 'original']
  : ['proxy', 'original'];

export function createPlayableMediaStore<PlaylistItemMeta>(
  playlistStore: ReturnType<typeof createPlaylistStore<PlaylistItemMeta>>,
  syncClient: SyncClient,
  playerEvents: PlayerEvents,
) {
  /**
   * The current time of the media.
   * Specified in seconds.
   */
  const $currentTime = atom<number | undefined>(undefined);

  /**
   * Time that the player should seek to
   * Specified in seconds.
   * Synced on load to leader currentTime.
   */
  const $targetTime = syncAtom<number | undefined>('targetTime', undefined, {
    syncClient,
    bootstrapStore: $currentTime,
  });

  /**
   * Seeking status.
   * Not synced on load
   */
  const $seeking = atom<boolean>(false);

  /**
   * The duration of the current media.
   * Specified in seconds.
   */
  const $duration = atom<number | undefined>(undefined);

  /**
   * Whether the current media is a live/growing asset whose duration increases
   * over time as new segments are ingested.
   */
  const $isLive = atom<boolean>(false);

  /**
   * Whether the media is still being encoded rather than ingested live. The
   * manifest grows either way, but this one has no live edge to follow
   */
  const $isEncoding = atom<boolean>(false);

  /** How much has been written, when that is less than the full duration */
  const $availableEnd = atom<number | undefined>(undefined);

  /**
   * Whether playback is at the live edge, ie. close enough to the end of the
   * seekable window to be treated as watching live rather than behind it
   */
  const $isAtLiveEdge = computed(
    [$isLive, $currentTime, $duration],
    (isLive, currentTime, duration) => {
      if (!isLive || currentTime === undefined || duration === undefined) {
        return false;
      }
      return duration - currentTime <= LIVE_EDGE_THRESHOLD_SECONDS;
    },
  );

  /**
   * Whether playback is following the live edge. The seekable window advances a
   * segment at a time, so this tracks intent rather than distance: it holds
   * until playback is deliberately moved off live, and is what the UI reflects
   */
  const $isFollowingLive = atom<boolean>(false);

  /**
   * The play intention of the player.
   */
  const $shouldBePlaying = syncAtom<boolean>('shouldBePlaying', false, {
    syncClient,
  });

  /**
   * The player is scrubbing the timeline.
   */
  const $scrubbing = atom<boolean>(false);

  /*
   * Stepping mode is active
   */
  const $stepping = atom<boolean>(false);

  /**
   * The playing status of the player.
   */
  const $playing = atom<boolean>(false);

  const $volume = persistentAtom<number>('UP_volume', 1, numberEncoder);

  /* Should the UI reflect time in timecode or frames */
  const $timeFormat = persistentAtom<TimeFormat>('UP_timeformat', 'timecode');

  function setTimeFormat(style: TimeFormat) {
    $timeFormat.set(style);
  }

  /**
   * The audio meter of the media.
   * Represents current volume level in dB, array indexes represent channel indexes.
   */
  const $audioMeter = atom<number[]>([]);

  /** If audio mixer is currently visible. */
  const $audioMixerVisible = persistentAtom<boolean>(
    'UP_audioMixerVisible',
    true,
    genericEncoder,
  );

  /**
   * Audio channel configuration
   * Represents volume, pan, mute and solo states for each audio channel.
   */
  const $audioChannels = persistentAtom<AudioChannel[]>(
    'UP_audioChannels',
    initialAudioChannels,
    createPersistentAudioChannelsEncoder(logger, initialAudioChannels),
  );

  /**
   * Preferred variant types in order of preference.
   */
  const $preferredVariants = persistentAtom<VariantType[]>(
    'UP_preferredVariants',
    initialPreferredVariants,
    genericEncoder,
  );

  /**
   * Currently selected variant based on preferences and playlist item available variants.
   */
  const $currentVariant = computed(
    [playlistStore.$currentPlaylistItem, $preferredVariants],
    getPreferredPlaylistItemVariant,
  );

  /** URL of the current playlist item or the current variant if available. */
  const $currentPlaylistItemVariantUrl = computed(
    [$currentVariant, playlistStore.$currentPlaylistItem],
    (currentVariant, currentItem) => {
      if (currentVariant?.url) {
        return currentVariant.url;
      }

      return currentItem?.url;
    },
  );

  /** Audio channel configurations for current playlist item variant. */
  const $activeAudioChannels = computed(
    [$audioChannels, $currentVariant],
    (audioChannels, currentVariant) => {
      const numberOfChannels = currentVariant?.audioChannels ?? 0;
      return audioChannels.slice(0, numberOfChannels);
    },
  );

  const $playbackRate = syncAtom<number>('playbackRate', 1, { syncClient });

  const $availablePlaybackRates = atom(availableRates);

  const $playbackTrajectory = computed(
    [$playbackRate],
    (currentPlaybackRate) => {
      if (currentPlaybackRate >= 1) {
        return 'forward';
      }
      if (currentPlaybackRate > 0 && currentPlaybackRate < 1) {
        return 'forward_slow';
      }
      if (currentPlaybackRate <= -1) {
        return 'backward';
      }
      if (currentPlaybackRate < 0 && currentPlaybackRate > -1) {
        return 'backward_slow';
      }
    },
  );

  const $unlockMediaPasswordStatus = atom<
    UnlockMediaPasswordStatus | undefined
  >(undefined);

  let updatePassword:
    | PlayerMediaPasswordEvent['detail']['updatePassword']
    | undefined;

  playerEvents.addEventListener('mediaPassword', (event) => {
    updatePassword = event.detail.updatePassword;
  });

  function setUnlockMediaPasswordStatus(
    status: UnlockMediaPasswordStatus | undefined,
  ) {
    $unlockMediaPasswordStatus.set(status);
    if (status === undefined) {
      updatePassword = undefined;
    }
  }

  /**
   * Function to provide a password in case if player requested it.
   */
  function providePassword(password: string) {
    if (!updatePassword) {
      logger.debug('Update password function is not set');
      return;
    }
    updatePassword(password);
  }

  function setNextPlaybackRateForTrajectory(trajectory: PlaybackTrajectory) {
    const currentRate = $playbackRate.get();
    const ratesForTrajectory = playbackRates[trajectory];

    const currentIndex = ratesForTrajectory.indexOf(currentRate);
    if (currentIndex === -1) {
      // If the current playbackRate is not part of the current trajectory, return first rate of that trajectory
      return ratesForTrajectory[0];
    }
    const resultRate =
      ratesForTrajectory[currentIndex + 1] ?? ratesForTrajectory[currentIndex];
    setPlaybackRate(resultRate);
  }

  function setInitialPlaybackRateForTrajectory(trajectory: PlaybackTrajectory) {
    const ratesForTrajectory = playbackRates[trajectory];
    setPlaybackRate(ratesForTrajectory[0]);
  }

  function handlePlaybackTrajectoryIncrease(trajectory: PlaybackTrajectory) {
    const currentTrajectory = $playbackTrajectory.get();
    const shouldBePlaying = $shouldBePlaying.get();

    if (currentTrajectory === trajectory && shouldBePlaying) {
      setNextPlaybackRateForTrajectory(trajectory);
    } else {
      setInitialPlaybackRateForTrajectory(trajectory);
    }

    play();
  }

  const $shouldLoop = syncAtom<boolean>('shouldLoop', false, { syncClient });

  const $subtitles = atom<Subtitle[]>([]);

  const $currentSubtitlesId = syncAtom<string | undefined>(
    'currentSubtitlesId',
    undefined,
    { syncClient },
  );

  const $currentSubtitles: ReadableAtom<Subtitle | undefined> = computed(
    [$subtitles, $currentSubtitlesId],
    (subtitles, currentId) => {
      return subtitles.find((subtitle) => subtitle.id === currentId);
    },
  );

  $subtitles.subscribe((subtitles) => {
    const currentSubtitlesId = $currentSubtitlesId.get();
    if (subtitles.some((subtitle) => subtitle.id === currentSubtitlesId)) {
      return;
    }
    const defaultSubtitle = subtitles.find((subtitle) => subtitle.default);
    $currentSubtitlesId.set(defaultSubtitle?.id); // Set default subtitle or undefined if not found
  });

  /**
   * We can get playableRange that exceeds the start and the end of the media
   * because of this there should be the additional correction.
   */
  const $playableRange = computed(
    [playlistStore.$currentPlaylistItem, $duration, $isLive],
    (currentItem, durationStoreValue, isLive) => {
      let duration = durationStoreValue;
      if (!currentItem) {
        duration = 0;
      }
      const playableRange = currentItem?.playableRange;
      if (!playableRange) {
        return {
          start: 0,
          // For live assets with no known duration yet use Infinity so clampTime
          // passes all finite times through and doesn't stop playback at the edge.
          end: duration ?? (isLive ? Infinity : 0),
        };
      }
      const start = Math.max(0, playableRange.start);
      const end = Math.min(duration ?? playableRange.end, playableRange.end);
      return {
        start: start > end ? 0 : start,
        end: end < 0 ? 0 : end,
      };
    },
  );

  /**
   * The time for the last frame of the playable range
   */
  const $lastPlayableFrameTime = computed(
    [$playableRange, playlistStore.$frameDuration],
    (playableRange, frameDuration) => {
      if (playableRange.end === 0) {
        return 0;
      }
      return playableRange.end - frameDuration;
    },
  );

  /**
   * The last frame written so far. The timeline still spans the full duration,
   * so this is only what a seek is held to
   */
  const $lastAvailableFrameTime = computed(
    [$availableEnd, playlistStore.$frameDuration],
    (availableEnd, frameDuration) => {
      if (availableEnd === undefined) {
        return undefined;
      }
      return Math.max(availableEnd - frameDuration, 0);
    },
  );

  const $lastFoundFrameTime = atom<number | undefined>(undefined);

  function setLastFoundFrameTime(time: number | undefined) {
    $lastFoundFrameTime.set(time);
  }

  const $smpteTimecode = computed(
    [$currentTime, playlistStore.$firstFrameOffset, $lastPlayableFrameTime],
    (currentTime, firstFrameOffset, lastPlayableFrameTime) => {
      const clampedTime = clamp(currentTime ?? 0, 0, lastPlayableFrameTime);
      const timecode = playlistStore.createTimecode({ seconds: clampedTime });
      timecode.addFrames(firstFrameOffset);
      return timecode.toSMPTE();
    },
  );

  function getFrameAlignedTime(time: number) {
    const item = playlistStore.$currentPlaylistItem.get();
    if (!item) {
      return 0;
    }
    return getAlignedTime(time, item);
  }

  function setCurrentTime(time: number | undefined) {
    $currentTime.set(time);
  }

  onSet($targetTime, ({ newValue }) => {
    // targetTime should update listeners even when it's the same value
    if (newValue === $targetTime.get()) {
      $targetTime.notify(newValue);
    }
  });

  // The current item is re-set whenever the playlist is rebuilt, which happens
  // while the same media keeps playing, so live only resets on a real item change
  let currentItemId = playlistStore.$currentPlaylistItemId.get();
  onSet(playlistStore.$currentPlaylistItem, () => {
    const itemId = playlistStore.$currentPlaylistItemId.get();
    if (itemId === currentItemId) {
      return;
    }
    currentItemId = itemId;
    setIsLive(false);
    setIsEncoding(false);
    setAvailableEnd(undefined);
  });

  function getTimeFromSMPTE(timecode: string): number {
    // Remove any non-numeric characters
    let value = timecode.replace(/\D/g, '');
    const currentItem = playlistStore.$currentPlaylistItem.get();

    // We treat audio frames as milliseconds.
    const digits = currentItem?.contentType === 'audio' ? 9 : 8;
    value = value.slice(-digits).padStart(digits, '0');

    const framesSeparator = currentItem?.isDropFrame ? ';' : ':';

    value = `${value.slice(0, 2)}:${value.slice(2, 4)}:${value.slice(4, 6)}${framesSeparator}${value.slice(6, digits)}`;

    const timecodeObject = playlistStore.createTimecode({ smpte: value });
    timecodeObject.subtractFrames(playlistStore.$firstFrameOffset.get());

    let seconds = timecodeObject.toSeconds();
    const duration = $duration.get();

    if (duration !== undefined) {
      seconds = Math.min(seconds, duration);
    }

    return seconds;
  }

  function getTimeFromFrame(frame: number): number {
    const frameDuration = playlistStore.$frameDuration.get();

    return (frame - 1) * frameDuration;
  }

  function setSeeking(seeking: boolean) {
    $seeking.set(seeking);
  }

  function setIsLive(isLive: boolean) {
    $isLive.set(isLive);
    $isFollowingLive.set(isLive);
  }

  function setIsEncoding(isEncoding: boolean) {
    $isEncoding.set(isEncoding);
  }

  // An encode only grows, so this never moves backwards
  function setAvailableEnd(availableEnd: number | undefined) {
    if (availableEnd === undefined || !Number.isFinite(availableEnd)) {
      $availableEnd.set(undefined);
      return;
    }
    const current = $availableEnd.get();
    if (current !== undefined && availableEnd <= current) {
      return;
    }
    $availableEnd.set(availableEnd);
  }

  function setFollowingLive(followingLive: boolean) {
    $isFollowingLive.set(followingLive);
  }

  function setDuration(duration: number) {
    if (!Number.isFinite(duration) || duration < 0) {
      $duration.set(undefined);
      return;
    }
    $duration.set(duration);
  }

  function setShouldBePlaying(shouldBePlaying: boolean) {
    $shouldBePlaying.set(shouldBePlaying);
  }

  function setScrubbing(scrubbing: boolean) {
    $scrubbing.set(scrubbing);
  }

  function setStepping(stepping: boolean) {
    $stepping.set(stepping);
  }

  function setPlaying(playing: boolean) {
    $playing.set(playing);
  }

  function setVolume(volume: number) {
    if (volume < 0 || volume > 1) {
      logger.debug('Invalid volume value. It must be between 0.0 and 1.0.');
      return;
    }
    $volume.set(volume);
  }

  function setPlaybackRate(rate: number) {
    $playbackRate.set(rate);
  }

  function toggleShouldLoop() {
    $shouldLoop.set(!$shouldLoop.get());
  }

  function play() {
    $shouldBePlaying.set(true);
  }

  function pause() {
    const playbackRate = $playbackRate.get();
    // If playing in reverse, pausing should set you back to playing forwards at 1x
    if (playbackRate < 0) {
      setPlaybackRate(1);
    }
    $isFollowingLive.set(false);
    $shouldBePlaying.set(false);
  }

  function togglePlay() {
    if (!$shouldBePlaying.get()) {
      play();
    } else {
      pause();
    }
  }

  function setSubtitles(subtitles: Subtitle[] | undefined) {
    $subtitles.set(subtitles ?? []);
  }

  function setCurrentSubtitlesId(trackId: string | undefined) {
    $currentSubtitlesId.set(trackId);
  }

  function resetAudioMeter() {
    $audioMeter.set([]);
  }

  function setAudioMeter(values: number[]) {
    const currentValues = $audioMeter.get();
    if (values.every((value, index) => value === currentValues[index])) {
      return;
    }
    $audioMeter.set(values);
  }

  function setAudioChannels(channels: AudioChannel[]) {
    $audioChannels.set(channels);
  }

  function updateAudioChannel(index: number, channel: Partial<AudioChannel>) {
    const currentChannels = $audioChannels.get() ?? initialAudioChannels;
    if (index < 0 || index >= currentChannels.length) {
      logger.debug('Invalid audio channel index');
      return;
    }
    const updatedChannels = currentChannels.map(
      (currentChannel, currentIndex) =>
        currentIndex === index
          ? { ...currentChannel, ...channel }
          : currentChannel,
    );
    setAudioChannels(updatedChannels);
  }

  function setAudioMixerVisible(visible: boolean) {
    $audioMixerVisible.set(visible);
  }

  function setPreferredVariant(variant: VariantType) {
    const nextVariants = $preferredVariants.get().filter((v) => v !== variant);
    nextVariants.unshift(variant);
    $preferredVariants.set(nextVariants);
  }

  return {
    $currentTime,
    $targetTime,
    $smpteTimecode,
    $seeking,
    $duration,
    $isLive,
    $isEncoding,
    $availableEnd,
    $isAtLiveEdge,
    $isFollowingLive,
    $shouldBePlaying,
    $scrubbing,
    $stepping,
    $playing,
    $volume,
    $playbackRate,
    $playbackTrajectory,
    $preferredVariants,
    $currentVariant,
    $currentPlaylistItemVariantUrl,
    $availablePlaybackRates,
    $shouldLoop,
    $playableRange,
    $lastPlayableFrameTime,
    $lastAvailableFrameTime,
    $lastFoundFrameTime,
    $subtitles,
    $currentSubtitlesId,
    $currentSubtitles,
    $audioMeter,
    $audioChannels,
    $activeAudioChannels,
    $audioMixerVisible,
    $timeFormat,
    $unlockMediaPasswordStatus,
    setCurrentTime,
    getTimeFromSMPTE,
    getTimeFromFrame,
    setSeeking,
    setIsLive,
    setIsEncoding,
    setAvailableEnd,
    setFollowingLive,
    setDuration,
    setShouldBePlaying,
    setScrubbing,
    setStepping,
    setPlaying,
    setVolume,
    setPlaybackRate,
    setPreferredVariant,
    handlePlaybackTrajectoryIncrease,
    toggleShouldLoop,
    play,
    pause,
    togglePlay,
    setLastFoundFrameTime,
    getFrameAlignedTime,
    setSubtitles,
    setCurrentSubtitlesId,
    setAudioMeter,
    resetAudioMeter,
    setAudioChannels,
    setAudioMixerVisible,
    updateAudioChannel,
    setTimeFormat,
    setUnlockMediaPasswordStatus,
    providePassword,
  };
}
